<template>
  <EditorPopover ref="popover" @cancel="cancelEditing">

    <TabbedBox>
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-panel" ref="tabPanel">
          <i class="far fa-clipboard-list-check"></i> Assembly
        </Tab>
        <Tab tabid="tab-tools" v-if="isDeveloperUser" ref="developerTab">
          <i class="far fa-tools"></i> Developer
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-panel">
          <div class="row" v-if="itemData">
            <div class="col-6">
              <EditorPopoverDataRow label="Stock">{{itemStockData.name}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Mark">{{itemData.mark}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Quantity">{{itemData.quantity}}</EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeNCRLocked"><span class="text-danger">NCR Not Selected</span></EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeCancelled"><span class="text-danger">Cancelled Item</span></EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeInvoiced"><span class="text-danger">Invoiced Item</span></EditorPopoverDataRow>
              <EditorPopoverDataRow label="Status" v-if="isShapeDespatched"><span class="text-danger">Despatched Item</span></EditorPopoverDataRow>
            </div>
            <div class="col-6">
              
            </div>
          </div>

          <EditorPopoverButtonList>
            <EditorPopoverButton v-if="isShapeEditable && isRemoveAvailable" title="Remove" type="remove" icon="fa-trash-alt" @click="popoverActionButtonClick('remove')" />
            <EditorPopoverButton v-if="isShapeEditable && isCreateLibraryShapeAvailable" title="Create Library Shape" type="action" icon="fa-layer-plus" @click="popoverActionButtonClick('createLibraryShape')" />
            <EditorPopoverButton v-if="isShapeEditable" title="Update" type="edit" icon="fa-pencil" @click="popoverActionButtonClick('update')" />
          </EditorPopoverButtonList>
        </TabPage>

        <TabPage tabid="tab-tools" v-if="isDeveloperUser">
          <div class="row">
            <div class="col-6">
              <EditorPopoverButton class="mb-2" title="Log Assembly" type="action" icon="fa-code" @click="developerLogAssembly" /><br />              
            </div>
            <div class="col-6">
              
            </div>
          </div>
        </TabPage>
      </template>
    </TabbedBox>  
    
  </EditorPopover>
</template>



<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import EditorPopover from '@glass-designer/components/editorPopover'
import EditorPopoverMixin from '@glass-designer/mixins/editorPopover'
import EditorPopoverDataRow from '@glass-designer/components/editorPopoverDataRow'
import EditorKeypad from '@glass-designer/components/editorKeypad'
import MathsUtils from '@/utils/mathsUtils'

import EditorPopoverButtonList from '@glass-designer/components/popover/editorPopoverButtonList'
import EditorPopoverButton from '@glass-designer/components/popover/editorPopoverButton'

import { LibraryItemShapeTypes } from '@glass-designer/types'
import { OrderEditorCutoutTypes, StockFaceTypeLookup, StockGrainDirectionTypeLookup } from '@/store/types'

export default {
  name: 'EditorAssemblyPopover',
  mixins: [EditorPopoverMixin],
  components: {
    EditorPopover,
    EditorPopoverDataRow,
    EditorPopoverButtonList,
    EditorPopoverButton
  },
  props: {
    editingEnabled: {
      type: Boolean,
      default: true
    },
  },
  data() {
    return {
      toolInfoText: '',
      name: '',
      assembly: null,
      itemData: {},
      isRemoveAvailable: true,
      isCloneAvailable: true,
      shapeInfo: {
        width: 0,
        height: 0,
        area: 0,
        weight: 0,
        thickness: 0,
        perimeter: 0,
        straightCutCount: 0,
        cornerCutoutCount: 0,
        edgeCutoutCount: 0,
        centerCutoutCount: 0,
        splitCount: 0
      }
    }
  },
  computed: {
    itemStockData() {
      if(this.itemData == null)
        return {}
      return this.itemData.stock || {}
    },
    itemPriceGroupData() {
      if(this.itemData == null)
        return {}
      return this.itemData.stockPriceGroup || {}
    },
    isCreateLibraryShapeAvailable() {
      if(this.itemData == null)
        return false

      return true
    },
    isStockLinked() {
      return this.itemPriceGroupData.isStockLinked
    },
    isRotateAvailable() {
      // Don't rotate if stock linked
      if(this.isStockLinked)
        return false
      return true    
    },
    glassSplitList() {
      if(!this.shape)
        return []
      let splitRectangles = this.assembly.calculateSplittingRectangles()

      return splitRectangles.map(rectangle => {
        return `${Math.round(rectangle.max.y - rectangle.min.y)}mm x ${Math.round(rectangle.max.x - rectangle.min.x)}mm`
      })
    },
    isShapeNCRLocked() {
      if(this.shape)
        return this.assembly.isNCRLocked()
      return false
    },
    isShapeCancelled() {
      if(this.shape)
        return this.assembly.isCancelled()
      return false
    },
    isShapeInvoiced() {
      if(this.shape)
        return this.assembly.isInvoiced()
      return false
    },
    isShapeDespatched() {
      if(this.shape)
        return this.assembly.isDespatched()
      return false
    },
    isShapeEditable() {
      if(!this.editingEnabled)
        return false
      if(this.shape) {
        if(!this.assembly.isItemEditable())
          return false
      }
      return true
    },
    isStockUsingFaces() {
      return !!this.itemStockData.isStockUsingFaces
    },
    isStockUsingGrain() {
      return !!this.itemStockData.isStockUsingGrain
    }
  },
  watch: {
    
  },
  created() {
    this.StockFaceTypeLookup = StockFaceTypeLookup
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  methods: {
    setup(editor, options) {
      this.clearOptions()

      this.name = options.name || 'Assembly'
      this.itemData = options.itemData || null
      this.assembly = options.assembly || null
      this.isRemoveAvailable = !!options.isRemoveAvailable
      this.isCloneAvailable = !!options.isCloneAvailable

      // Setup callbacks next tick so they don't get called when resetting values
      this.callbackContext = options.context
      this.updateCallback = options.updateCallback
      this.actionCallback = options.actionCallback
      this.applyCallback = options.applyCallback
      this.cancelCallback = options.cancelCallback

      this.$nextTick(() => {
        this.$refs.tabPanel.show()
      })
    },
    clearOptions() {
      this.itemData = null

      this.callbackContext = null
      this.updateCallback = null
      this.actionCallback = null
      this.applyCallback = null
      this.cancelCallback = null
    },
    displayToolPopover(containerEl, editor, displayOptions) {
      this.$refs.popover.displayToolPopover(containerEl, editor, displayOptions)
    },
    viewportUpdated(containerEl, editor) {
    },
    hide() {
      if(this.cancelCallback) {
        // Reset cancel CB so it only is called once when hiding popovers
        let cancelCB = this.cancelCallback
        this.cancelCallback = null

        cancelCB(this.callbackContext, false)
      }

      this.clearOptions()
      this.$refs.popover.hide()
    },
    cancelEditing() {
      if(this.cancelCallback)
        this.cancelCallback(this.callbackContext, true)

      this.clearOptions()
      this.hide()
    },
    onShortcutKey(code) {
      switch(code) {
        case 'Escape':
          this.cancelEditing()
          break
        case 'Delete':
        case 'Backspace':
          if(this.isShapeEditable && this.isRemoveAvailable)
            this.popoverActionButtonClick('remove')
          break
      }
    },

    // Developer Tools
    developerLogAssembly() {
      console.log('Assembly Item Data')
      console.log(JSON.stringify(this.assembly.itemData, null, 2))
    }
  }
}
</script>